import React, { useState } from 'react';
import { uid } from '../db';
import { Btn, Card, Field, Grid, Input, Modal, ModalFooter, NoData, Select, toast } from '../ui.jsx';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TTC_COLORS = [
  { bg: 'rgba(0,32,69,0.08)', fg: '#002045', ring: 'rgba(0,32,69,0.25)' },
  { bg: 'rgba(25,96,163,0.10)', fg: '#1960a3', ring: 'rgba(25,96,163,0.25)' },
  { bg: 'rgba(5,150,105,0.10)', fg: '#059669', ring: 'rgba(5,150,105,0.25)' },
  { bg: 'rgba(180,83,9,0.10)', fg: '#b45309', ring: 'rgba(180,83,9,0.25)' },
  { bg: 'rgba(124,58,237,0.10)', fg: '#7c3aed', ring: 'rgba(124,58,237,0.25)' },
  { bg: 'rgba(8,145,178,0.10)', fg: '#0891b2', ring: 'rgba(8,145,178,0.25)' },
  { bg: 'rgba(186,26,26,0.10)', fg: '#ba1a1a', ring: 'rgba(186,26,26,0.25)' },
];

function Timetable({ db, save }) {
  const [cls, setCls] = useState('');
  const [open, setOpen] = useState(false);
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [form, setForm] = useState({ ty: 'subject', day: 'Monday' });
  const [editId, setEditId] = useState(null);
  const [slForm, setSlForm] = useState({ l: '', s: '', e: '' });
  const sf = k => v => setForm(f => ({ ...f, [k]: v }));
  const classes = db.classes.map(c => c.name);
  const pers = db.tt.filter(p => p.cls === cls);
  const scm = {}; let ci = 0;
  pers.forEach(p => { if (p.su && !scm[p.su]) { scm[p.su] = ci % TTC_COLORS.length; ci++; } });

  const savePer = () => {
    if (!form.cls) { toast('Select class', 'err'); return; }
    if (form.ty === 'subject' && !form.su?.trim()) { toast('Subject required', 'err'); return; }
    if (!form.sid) { toast('Select slot', 'err'); return; }
    const id = editId || 'P' + uid();
    // When editing: remove by id. When adding: remove any existing period in same slot/day/class
    let newTT;
    if (editId) {
      // Remove old entry by id, then add updated one
      newTT = db.tt.filter(p => p.id !== editId);
      // Also remove any conflicting period in the new slot (different id)
      newTT = newTT.filter(p => !(p.cls === form.cls && p.day === form.day && p.sid === form.sid));
    } else {
      // Remove any existing period in same slot
      newTT = db.tt.filter(p => !(p.cls === form.cls && p.day === form.day && p.sid === form.sid));
    }
    newTT.push({ id, cls: form.cls, day: form.day, sid: form.sid, ty: form.ty, su: form.ty === 'subject' ? form.su : form.ty, tea: form.tea || '', rm: form.rm || '' });
    save({ ...db, tt: newTT });
    if (form.cls !== cls) setCls(form.cls);
    setOpen(false); setEditId(null); toast('Saved');
  };
  const delPer = () => {
    if (!editId) return;
    save({ ...db, tt: db.tt.filter(p => p.id !== editId) });
    setOpen(false); setEditId(null); toast('Period removed', 'err');
  };
  const editPer = (p) => { setForm({ ...p }); setEditId(p.id); setOpen(true); };
  const qper = (day, sid) => { setForm({ ty: 'subject', day, sid, cls }); setEditId(null); setOpen(true); };
  const clearTT = () => { if (!cls) { toast('Select class', 'err'); return; } if (!window.confirm('Clear all periods for this class?')) return; save({ ...db, tt: db.tt.filter(p => p.cls !== cls) }); toast('Cleared', 'err'); };

  const addSlot = () => {
    if (!slForm.l || !slForm.s || !slForm.e) { toast('Fill all fields', 'err'); return; }
    save({ ...db, slots: [...db.slots, { id: 'SL' + uid(), l: slForm.l, s: slForm.s, e: slForm.e }] });
    setSlForm({ l: '', s: '', e: '' });
  };
  const saveEditSlot = () => {
    if (!editSlot || !editSlot.l || !editSlot.s || !editSlot.e) { toast('Fill all fields', 'err'); return; }
    save({ ...db, slots: db.slots.map(sl => sl.id === editSlot.id ? editSlot : sl) });
    setEditSlot(null); toast('Slot updated');
  };
  const delSlot = (id) => save({ ...db, slots: db.slots.filter(sl => sl.id !== id) });
  const resetSlots = () => { if (!window.confirm('Reset to defaults?')) return; save({ ...db, slots: [{ id: '1', l: 'Period 1', s: '08:00', e: '08:45' }, { id: '2', l: 'Period 2', s: '08:45', e: '09:30' }, { id: '3', l: 'Break', s: '09:30', e: '10:00' }, { id: '4', l: 'Period 3', s: '10:00', e: '10:45' }, { id: '5', l: 'Period 4', s: '10:45', e: '11:30' }, { id: '6', l: 'Lunch', s: '11:30', e: '12:15' }, { id: '7', l: 'Period 5', s: '12:15', e: '13:00' }, { id: '8', l: 'Period 6', s: '13:00', e: '13:45' }] }); };

  const isBreakSlot = (sl) => /break|lunch|recess|assembly/i.test(sl.l);
  const activeClass = db.classes.find(c => c.name === cls);
  const stuCount = cls ? db.students.filter(s => s.cls === cls).length : 0;
  const subCount = Object.keys(scm).length;
  const upcomingExam = db.exams.find(e => e.st === 'Upcoming' && (!e.cls || e.cls === cls));
  const inCls = 'px-3 py-2 bg-surface-container-low border-0 rounded-lg text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all';

  return (
    <div>
      {/* Page Header */}
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="text-4xl font-extrabold text-primary font-headline tracking-tight mb-2">Class Timetable</h1>
          <p className="text-on-surface-variant font-medium">Academic Session {db.settings.year || '2025-26'}</p>
        </div>
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant px-1">Class</label>
            <select value={cls} onChange={e => setCls(e.target.value)}
              className="bg-surface-container-lowest border-0 rounded-xl px-4 py-2.5 font-semibold text-primary shadow-sm ring-1 ring-outline-variant/20 focus:ring-primary focus:outline-none transition-all text-sm cursor-pointer">
              <option value="">Select Class</option>
              {classes.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setForm({ ty: 'subject', day: 'Monday', cls }); setEditId(null); setOpen(true); }}
              className="bg-primary text-on-primary px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:-translate-y-0.5 transition-all border-0 cursor-pointer shadow-lg shadow-primary/20">
              <span className="material-symbols-outlined text-sm">add</span> Add Period
            </button>
            <button onClick={() => setSlotsOpen(true)}
              className="bg-surface-container-lowest text-primary px-4 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-surface-container-low transition-all border-0 cursor-pointer shadow-sm ring-1 ring-outline-variant/20">
              <span className="material-symbols-outlined text-sm">schedule</span> Time Slots
            </button>
            {cls && <button onClick={clearTT}
              className="bg-error-container text-error px-4 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-error/15 transition-all border-0 cursor-pointer">
              <span className="material-symbols-outlined text-sm">delete_sweep</span> Clear
            </button>}
          </div>
        </div>
      </section>

      {/* Hero Focus Card */}
      {cls && (
        <div className="mb-10">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary-container p-8 text-on-primary shadow-xl shadow-primary/20">
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="flex items-center gap-6">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                  <span className="material-symbols-outlined text-5xl">schedule</span>
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-1 block">Class Overview</span>
                  <h2 className="text-3xl font-extrabold font-headline mt-1">{cls}</h2>
                  <p className="text-blue-200 font-medium mt-1">
                    {activeClass?.stream || 'General'}{activeClass?.room ? ` • Room ${activeClass.room}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6 bg-white/5 p-5 rounded-2xl border border-white/10 backdrop-blur-sm">
                <div className="text-center">
                  <p className="text-2xl font-black font-headline">{stuCount}</p>
                  <p className="text-xs text-blue-300 font-medium">Students</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-2xl font-black font-headline">{pers.length}</p>
                  <p className="text-xs text-blue-300 font-medium">Periods</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-2xl font-black font-headline">{subCount}</p>
                  <p className="text-xs text-blue-300 font-medium">Subjects</p>
                </div>
              </div>
            </div>
            <div className="absolute -right-16 -top-16 w-64 h-64 bg-secondary-container/10 rounded-full blur-3xl pointer-events-none"></div>
          </div>
        </div>
      )}

      {/* Timetable Grid */}
      {!cls ? (
        <div className="bg-surface-container-lowest rounded-2xl p-16 text-center">
          <span className="material-symbols-outlined text-5xl text-outline mb-4 block">schedule</span>
          <p className="text-lg font-bold text-primary font-headline mb-1">Select a class to view timetable</p>
          <p className="text-sm text-on-surface-variant">Choose a class from the dropdown above.</p>
        </div>
      ) : !db.slots.length ? (
        <div className="bg-surface-container-lowest rounded-2xl p-16 text-center">
          <span className="material-symbols-outlined text-5xl text-outline mb-4 block">timer</span>
          <p className="text-lg font-bold text-primary font-headline mb-1">No time slots configured</p>
          <button onClick={resetSlots} className="mt-4 bg-primary text-on-primary px-6 py-2.5 rounded-xl font-bold text-sm border-0 cursor-pointer">Load Default Slots</button>
        </div>
      ) : (
        <>
          {/* Desktop Grid */}
          <div className="hidden md:block rounded-3xl overflow-hidden shadow-sm border border-outline-variant/10 bg-surface-container-low mb-10">
            <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(6,1fr)' }}>
              <div className="h-20 flex items-center justify-center bg-surface-container text-[10px] font-bold uppercase tracking-widest text-on-surface-variant border-b border-outline-variant/20">
                Periods
              </div>
              {DAYS.map(day => (
                <div key={day} className="h-20 flex flex-col items-center justify-center border-b border-outline-variant/20 bg-surface-container-lowest">
                  <span className="text-xs font-bold text-on-surface-variant">{day.slice(0, 3).toUpperCase()}</span>
                </div>
              ))}
              {db.slots.map(sl => {
                const isBreak = isBreakSlot(sl);
                return (
                  <React.Fragment key={sl.id}>
                    <div className={`flex flex-col items-center justify-center border-b border-outline-variant/10 px-2 py-3 ${isBreak ? 'bg-tertiary-fixed/10' : 'bg-surface-container/30'}`}>
                      <span className={`font-bold text-xs ${isBreak ? 'text-on-tertiary-fixed-variant italic' : 'text-primary'}`}>{sl.l}</span>
                      <span className="text-[10px] text-on-surface-variant mt-0.5">{sl.s}–{sl.e}</span>
                    </div>
                    {DAYS.map(day => {
                      const p = pers.find(x => x.day === day && x.sid === sl.id);
                      if (isBreak && !p) {
                        return <div key={day} className="border-b border-outline-variant/10 bg-tertiary-fixed/5 p-2 flex items-center justify-center">
                          <span className="text-[10px] font-black uppercase text-on-tertiary-fixed-variant tracking-widest opacity-40">{sl.l}</span>
                        </div>;
                      }
                      if (p) {
                        const isSpecial = p.ty === 'break' || p.ty === 'assembly' || p.ty === 'free';
                        const col = isSpecial ? null : TTC_COLORS[scm[p.su] ?? 0];
                        const lb = p.ty === 'break' ? 'Break' : p.ty === 'assembly' ? 'Assembly' : p.ty === 'free' ? 'Free Period' : p.su || '—';
                        return <div key={day} className="border-b border-outline-variant/10 bg-surface-container-lowest p-2">
                          <div onClick={() => editPer(p)} className="h-[80px] rounded-xl p-3 flex flex-col justify-between cursor-pointer transition-all hover:ring-2 hover:ring-primary/20"
                            style={col ? { background: col.bg, color: col.fg } : { background: 'rgba(241,244,246,0.8)', color: 'var(--di)' }}>
                            <p className="text-xs font-bold leading-tight">{lb}</p>
                            {p.tea && <p className="text-[10px] opacity-70">{p.tea.split(' ')[0]}</p>}
                            {p.rm && <p className="text-[10px] opacity-50">Rm {p.rm}</p>}
                          </div>
                        </div>;
                      }
                      return <div key={day} className="border-b border-outline-variant/10 bg-surface-container-lowest p-2">
                        <div onClick={() => qper(day, sl.id)} className="h-[80px] rounded-xl border-2 border-dashed border-outline-variant/30 flex items-center justify-center cursor-pointer hover:border-primary/30 transition-all group">
                          <span className="material-symbols-outlined text-outline-variant/50 group-hover:text-primary/40 text-xl transition-colors">add</span>
                        </div>
                      </div>;
                    })}
                  </React.Fragment>
                );
              })}
            </div>
            {Object.keys(scm).length > 0 && (
              <div className="flex gap-4 flex-wrap px-6 py-4 bg-surface-container-lowest border-t border-outline-variant/10">
                {Object.keys(scm).map(su => {
                  const col = TTC_COLORS[scm[su]];
                  return <div key={su} className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                    <div className="w-3 h-3 rounded" style={{ background: col.bg, border: '1.5px solid ' + col.fg }}></div>{su}
                  </div>;
                })}
              </div>
            )}
          </div>

          {/* Mobile: Day-by-day cards */}
          <div className="md:hidden space-y-4 mb-10">
            {DAYS.map(day => {
              const dayPers = db.slots.map(sl => ({ sl, p: pers.find(x => x.day === day && x.sid === sl.id) }));
              return (
                <div key={day} className="bg-surface-container-lowest rounded-2xl overflow-hidden border border-outline-variant/10">
                  <div className="bg-primary px-4 py-3 flex items-center justify-between">
                    <span className="font-bold text-on-primary text-sm">{day}</span>
                    <button onClick={() => { setForm({ ty: 'subject', day, cls }); setEditId(null); setOpen(true); }}
                      className="text-on-primary/70 hover:text-on-primary border-0 bg-transparent cursor-pointer p-1">
                      <span className="material-symbols-outlined text-sm">add</span>
                    </button>
                  </div>
                  <div className="divide-y divide-outline-variant/10">
                    {dayPers.map(({ sl, p }) => {
                      const isBreak = isBreakSlot(sl);
                      if (isBreak && !p) return (
                        <div key={sl.id} className="px-4 py-2 bg-tertiary-fixed/5 flex items-center gap-3">
                          <span className="text-[10px] font-bold uppercase text-on-tertiary-fixed-variant tracking-widest w-20 shrink-0">{sl.s}</span>
                          <span className="text-xs text-on-tertiary-fixed-variant italic">{sl.l}</span>
                        </div>
                      );
                      if (p) {
                        const isSpecial = p.ty === 'break' || p.ty === 'assembly' || p.ty === 'free';
                        const col = isSpecial ? null : TTC_COLORS[scm[p.su] ?? 0];
                        const lb = p.ty === 'break' ? 'Break' : p.ty === 'assembly' ? 'Assembly' : p.ty === 'free' ? 'Free Period' : p.su || '—';
                        return (
                          <div key={sl.id} onClick={() => editPer(p)} className="px-4 py-3 flex items-center gap-3 cursor-pointer active:bg-surface-container-low"
                            style={col ? { borderLeft: `3px solid ${col.fg}` } : { borderLeft: '3px solid #94a3b8' }}>
                            <span className="text-[10px] text-on-surface-variant w-20 shrink-0">{sl.s}–{sl.e}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold truncate" style={col ? { color: col.fg } : {}}>{lb}</p>
                              {p.tea && <p className="text-[10px] text-on-surface-variant truncate">{p.tea}</p>}
                            </div>
                            {p.rm && <span className="text-[10px] text-on-surface-variant shrink-0">Rm {p.rm}</span>}
                            <span className="material-symbols-outlined text-outline-variant text-sm shrink-0">edit</span>
                          </div>
                        );
                      }
                      return (
                        <div key={sl.id} onClick={() => qper(day, sl.id)} className="px-4 py-3 flex items-center gap-3 cursor-pointer active:bg-surface-container-low">
                          <span className="text-[10px] text-on-surface-variant w-20 shrink-0">{sl.s}–{sl.e}</span>
                          <span className="text-xs text-outline-variant">{sl.l}</span>
                          <span className="material-symbols-outlined text-outline-variant/50 text-sm ml-auto">add</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Footer Stats */}
      {cls && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-surface-container-lowest rounded-2xl p-6">
            <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-4">Class Overview</p>
            <div className="flex items-center justify-between">
              <div><p className="text-2xl font-black text-primary font-headline">{stuCount}</p><p className="text-xs text-on-surface-variant">Students Enrolled</p></div>
              <div className="h-10 w-px bg-outline-variant/30"></div>
              <div><p className="text-2xl font-black text-primary font-headline">{subCount}</p><p className="text-xs text-on-surface-variant">Subjects Weekly</p></div>
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-2xl p-6 flex items-center gap-4">
            <div className="bg-primary-container text-on-primary-container h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined">event_note</span>
            </div>
            <div>
              <p className="text-sm font-bold text-primary">Upcoming Exam</p>
              <p className="text-xs text-on-surface-variant">{upcomingExam ? upcomingExam.name + (upcomingExam.dt ? ' — ' + upcomingExam.dt : '') : 'No upcoming exams'}</p>
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-2xl p-6 flex items-center gap-4">
            <div className="bg-tertiary-fixed text-on-tertiary-fixed h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined">auto_awesome</span>
            </div>
            <div>
              <p className="text-sm font-bold text-primary">Timetable Status</p>
              <p className="text-xs text-on-surface-variant">{pers.length} periods configured for {cls}</p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Period Modal */}
      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? 'Edit Period' : 'Add Period'}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Class *"><Select value={form.cls || ''} onChange={sf('cls')}><option value="">—</option>{classes.map(c => <option key={c}>{c}</option>)}</Select></Field>
          <Field label="Day *"><Select value={form.day || 'Monday'} onChange={sf('day')}>{DAYS.map(d => <option key={d}>{d}</option>)}</Select></Field>
          <Field label="Time Slot *"><Select value={form.sid || ''} onChange={sf('sid')}><option value="">—</option>{db.slots.map(sl => <option key={sl.id} value={sl.id}>{sl.l} ({sl.s}–{sl.e})</option>)}</Select></Field>
          <Field label="Type"><Select value={form.ty || 'subject'} onChange={sf('ty')}><option value="subject">Subject</option><option value="break">Break</option><option value="assembly">Assembly</option><option value="free">Free Period</option></Select></Field>
          {(form.ty === 'subject' || !form.ty) && <>
            <Field label="Subject *"><Input value={form.su || ''} onChange={sf('su')} placeholder="Enter subject name" /></Field>
            <Field label="Teacher"><Select value={form.tea || ''} onChange={sf('tea')}><option value="">—</option>{db.teachers.map(t => <option key={t.id} value={t.fn + ' ' + t.ln}>{t.fn} {t.ln}{t.su ? ' (' + t.su + ')' : ''}</option>)}</Select></Field>
          </>}
          <Field label="Room"><Input value={form.rm || ''} onChange={sf('rm')} placeholder="Enter room number" /></Field>
        </div>
        <ModalFooter>
          {editId && <Btn variant="danger" onClick={delPer}>Delete</Btn>}
          <Btn onClick={() => { setOpen(false); setEditId(null); }}>Cancel</Btn>
          <Btn variant="primary" onClick={savePer}>Save</Btn>
        </ModalFooter>
      </Modal>

      {/* Time Slots Modal */}
      <Modal open={slotsOpen} onClose={() => { setSlotsOpen(false); setEditSlot(null); }} title="Manage Time Slots">
        <div className="space-y-2 mb-4 max-h-64 overflow-y-auto pr-1">
          {db.slots.map(sl => (
            <div key={sl.id}>
              {editSlot?.id === sl.id ? (
                <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-xl ring-1 ring-primary/20">
                  <input value={editSlot.l} onChange={e => setEditSlot(s => ({ ...s, l: e.target.value }))} placeholder="Label" className={inCls + ' flex-1 min-w-0'} />
                  <input type="time" value={editSlot.s} onChange={e => setEditSlot(s => ({ ...s, s: e.target.value }))} className={inCls + ' w-28'} />
                  <input type="time" value={editSlot.e} onChange={e => setEditSlot(s => ({ ...s, e: e.target.value }))} className={inCls + ' w-28'} />
                  <button onClick={saveEditSlot} className="px-3 py-2 bg-primary text-on-primary rounded-lg text-xs font-bold border-0 cursor-pointer flex-shrink-0">Save</button>
                  <button onClick={() => setEditSlot(null)} className="px-3 py-2 bg-surface-container text-on-surface rounded-lg text-xs font-bold border-0 cursor-pointer flex-shrink-0">✕</button>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-surface-container-low rounded-xl hover:bg-surface-container transition-colors">
                  <div className="flex-1">
                    <span className="font-semibold text-on-surface text-sm">{sl.l}</span>
                    <span className="text-on-surface-variant text-xs ml-2">{sl.s}–{sl.e}</span>
                  </div>
                  <button onClick={() => setEditSlot({ ...sl })} className="p-1.5 text-primary hover:bg-primary/10 rounded-lg border-0 bg-transparent cursor-pointer transition-colors">
                    <span className="material-symbols-outlined text-sm">edit</span>
                  </button>
                  <button onClick={() => delSlot(sl.id)} className="p-1.5 text-error hover:bg-error-container rounded-lg border-0 bg-transparent cursor-pointer transition-colors">
                    <span className="material-symbols-outlined text-sm">delete</span>
                  </button>
                </div>
              )}
            </div>
          ))}
          {!db.slots.length && <p className="text-sm text-on-surface-variant text-center py-4">No slots yet.</p>}
        </div>
        <div className="border-t border-surface-container-high pt-4 mb-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-3">Add New Slot</p>
          <div className="flex gap-2 flex-wrap">
            <input value={slForm.l} onChange={e => setSlForm(f => ({ ...f, l: e.target.value }))} placeholder="Label (e.g. Period 1)" className={inCls + ' flex-1 min-w-[120px]'} />
            <input type="time" value={slForm.s} onChange={e => setSlForm(f => ({ ...f, s: e.target.value }))} className={inCls + ' w-28'} />
            <input type="time" value={slForm.e} onChange={e => setSlForm(f => ({ ...f, e: e.target.value }))} className={inCls + ' w-28'} />
            <button onClick={addSlot} className="px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold border-0 cursor-pointer flex-shrink-0">Add</button>
          </div>
        </div>
        <ModalFooter>
          <Btn onClick={resetSlots}>Reset Defaults</Btn>
          <Btn variant="primary" onClick={() => { setSlotsOpen(false); setEditSlot(null); }}>Done</Btn>
        </ModalFooter>
      </Modal>
    </div>
  );
}


export { TTC_COLORS };
export default Timetable;
